/**
 * Media delivery URL construction for vibesites.
 * Ported from client/src/utils/media.ts — trimmed to rendering essentials.
 */

const MEDIA_HOST = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_MEDIA_HOST)
  || 'https://media.multihub.io';
const OWNED_MEDIA_HOSTS = [
  'https://media.multihub.io',
  'https://multihubmedia.com',
];
const MEDIA_HOSTNAMES = new Set([
  new URL(MEDIA_HOST).hostname,
  ...OWNED_MEDIA_HOSTS.map((host) => new URL(host).hostname),
]);
const USE_TRANSFORMS = !MEDIA_HOST.includes('r2.dev');

// Width buckets matching global/utils/media/delivery.js so CF caches are shared.
export const WIDTH_PRESETS = {
  THUMB: 160,
  XS: 320,
  SM: 640,
  MD: 960,
  LG: 1280,
  XL: 1920,
  DEFAULT: 1280,
  ALL: [160, 320, 640, 960, 1280, 1920],
} as const;

export const MEDIA_CLASSIFICATIONS = [
  'Community',
  'Apartment',
  'Lifestyle',
  'Floorplan',
  'Logo',
  'Other',
] as const;

export type MediaClassification = (typeof MEDIA_CLASSIFICATIONS)[number];

function snapToPreset(width: number): number {
  return WIDTH_PRESETS.ALL.find(p => p >= width) || WIDTH_PRESETS.XL;
}

/**
 * Build a canonical delivery URL from a known R2 storage key.
 *
 * Use this when the data already has `storage_key`. Visible images should
 * usually use `<VibesitesImage>` instead so they also get srcSet, sizes, and
 * fallback behavior.
 */
export function getMediaUrl(storageKey: string, options: { width?: number; quality?: number } = {}): string {
  if (!storageKey) return '';
  if (!USE_TRANSFORMS) return `${MEDIA_HOST}/${storageKey}`;

  const transforms = ['format=auto'];
  if (options.width) transforms.push(`w=${snapToPreset(options.width)}`);
  if (options.quality) transforms.push(`quality=${options.quality}`);
  return `${MEDIA_HOST}/cdn-cgi/image/${transforms.join(',')}/${storageKey}`;
}

/** Direct URL without CDN image transforms — use for copy-link, downloads, etc. */
export function getRawMediaUrl(storageKey: string): string {
  if (!storageKey) return '';
  return `${MEDIA_HOST}/${storageKey}`;
}

/**
 * Storage key of a video's poster frame. MH ingest writes it next to the video
 * as `{publicId}_thumb.webp`; prefer an explicit `thumbnail_key` when the asset
 * snapshot provides one.
 */
export function videoThumbStorageKey(videoStorageKey: string | null | undefined): string | null {
  if (!videoStorageKey) return null;
  const lastSlash = videoStorageKey.lastIndexOf('/');
  const filename = lastSlash >= 0 ? videoStorageKey.slice(lastSlash + 1) : videoStorageKey;
  const prefix = lastSlash >= 0 ? videoStorageKey.slice(0, lastSlash + 1) : '';
  const publicId = filename.replace(/\.[^.]+$/, '');
  if (!publicId) return null;
  return `${prefix}${publicId}_thumb.webp`;
}

/**
 * Build a responsive srcSet for a known R2 storage key.
 *
 * This is used by `<VibesitesImage>`. Most component code should not need to
 * call it directly unless it is implementing a custom image renderer.
 */
export function getMediaSrcSet(storageKey: string, quality?: number): string {
  if (!storageKey || !USE_TRANSFORMS) return '';
  return WIDTH_PRESETS.ALL.map((w) => `${getMediaUrl(storageKey, { width: w, quality })} ${w}w`).join(', ');
}

/**
 * Extract the storage key from a full CDN URL. Returns null if the URL
 * isn't from our media host.
 *
 * Handles both transformed and bare URLs:
 *   https://media.multihub.io/cdn-cgi/image/format=auto,w=1280/company/123/abc.webp → company/123/abc.webp
 *   https://multihubmedia.com/company/123/abc.webp                                  → company/123/abc.webp
 */
export function parseStorageKey(url: string | null): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (!MEDIA_HOSTNAMES.has(parsed.hostname)) return null;
    const pathname = parsed.pathname.replace(/^\/+/, '');
    if (!pathname) return null;

    const transformMatch = pathname.match(/^cdn-cgi\/image\/[^/]+\/(.+)$/);
    return decodeURIComponent(transformMatch?.[1] || pathname);
  } catch {
    return null;
  }
}

/**
 * Normalize an arbitrary URL for non-visual public output.
 *
 * If the URL belongs to Multihub media, including legacy hosts, it is re-emitted
 * through the current canonical media host. External URLs pass through
 * unchanged. Use this for metadata, JSON-LD, feeds, and other string-only
 * outputs. Visible images should use `<VibesitesImage>`.
 */
export function normalizeMediaUrl(
  url: string | null | undefined,
  options: { width?: number; quality?: number } = {},
): string | null {
  if (url == null) return null;
  if (url === '') return '';
  const storageKey = parseStorageKey(url);
  return storageKey ? getMediaUrl(storageKey, options) : url;
}

export function formatFileSize(bytes?: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
